/**
 * Validates action parameter bindings before they reach the designer
 * runtime. Catches a specific failure mode seen in production logs
 * where the LLM wires a NAVIGATE action's target-screen parameter to a
 * non-existent screen input:
 *
 *   {
 *     actionName: "NAVIGATE",
 *     parameterName: "toScreenId",
 *     bindingType: "default",
 *     source: "screenInput:toScreenId"   // ← illegal, screen has no such input
 *   }
 *
 * Runtime NAVIGATE expects `toScreenId` to resolve to a concrete screen
 * (a literal screenId, a `screen:<name>` reference, or a plain-string
 * fixed value). Binding it to a `screenInput:*` source silently fails
 * at dispatch because the target screen has no such input parameter,
 * leaving the Edit/View button as a dead click.
 *
 * Returned errors are surfaced to the LLM so it can re-bind correctly
 * instead of silently producing a broken flow. The validator is
 * intentionally narrow — it only rejects the specific prefixes that
 * are always wrong for these parameters. Callers fall through to the
 * existing designer pipeline for anything it does not flag.
 */

export interface BindingCandidate {
  readonly actionName?: string;
  readonly parameterName?: string;
  readonly bindingType?: string;
  readonly source?: string;
}

export interface BindingValidationError {
  readonly parameterName: string;
  readonly actionName: string;
  readonly reason: string;
  readonly suggestion: string;
}

const NAVIGATION_TARGET_PARAMS = new Set(["toScreenId", "toUrl"]);
const NAVIGATE_ACTION_NAMES = new Set(["NAVIGATE", "Navigate"]);

/**
 * Returns `true` when the binding belongs to a NAVIGATE action that
 * targets one of the guarded parameters. The guard only fires when the
 * action is actually NAVIGATE — bindings for other actions that happen
 * to have parameters named `toScreenId` / `toUrl` are left to their
 * own validation, since the screenInput/param illegal-source rule is
 * NAVIGATE-specific.
 *
 * When `actionName` is missing from the binding payload (the batched
 * set_multiple_parameter_bindings call doesn't always propagate it
 * per-item), we fall back to parameterName-based detection AND flag
 * the same prefixes — this matches the pre-review behaviour. The
 * actionName-aware path kicks in whenever the caller populates it,
 * which today covers the per-action-loop path that does know the
 * action kind.
 */
function shouldGuard(binding: BindingCandidate): boolean {
  const parameterName = (binding.parameterName ?? "").trim();
  if (!NAVIGATION_TARGET_PARAMS.has(parameterName)) return false;
  const actionName = (binding.actionName ?? "").trim();
  if (actionName.length === 0) return true;
  return NAVIGATE_ACTION_NAMES.has(actionName);
}

/**
 * Returns a BindingValidationError when the binding's source is
 * structurally invalid for a NAVIGATE target parameter, otherwise null.
 */
export function validateNavigateBinding(binding: BindingCandidate): BindingValidationError | null {
  if (!shouldGuard(binding)) {
    return null;
  }
  const parameterName = (binding.parameterName ?? "").trim();
  const source = (binding.source ?? "").trim();
  const actionName = binding.actionName ?? "";
  if (source.length === 0) {
    return null;
  }
  if (source.startsWith("screenInput:") || source.startsWith("param:") || source.startsWith("param.")) {
    return {
      parameterName,
      actionName,
      reason: `NAVIGATE.${parameterName} cannot bind from "${source}" — screen inputs belong to the target screen, not to the NAVIGATE parameter itself.`,
      suggestion:
        parameterName === "toScreenId"
          ? 'Set source to the concrete target screen (e.g. "<screenId>" or "screen:<ScreenName>") and pass the row identifier via the targetScreenInputParams array.'
          : "Set source to a fixed URL string or bind via componentValue."
    };
  }
  if (source.startsWith("current.") && parameterName === "toScreenId") {
    return {
      parameterName,
      actionName,
      reason: 'NAVIGATE.toScreenId must point at a concrete screen, not a row field like "current.*".',
      suggestion:
        'Bind toScreenId to "<screenId>" or "screen:<ScreenName>" — pass current.<pkField> via targetScreenInputParams instead.'
    };
  }
  return null;
}
